import { SVButton, SVLoader } from '@/components/core';
import ExternalAccountStatusLabel from '@/components/ExternalAccountStatusLabel';
import {
  BankApplicationDetails,
  BankApplicationFormValues,
} from '@/types/bankApplications';
import { ExternalAccountListItem } from '@/types/externalAccounts';
import { Box, Card, Typography } from '@mui/material';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';

type BankAccountApplicationExternalAccountsProps = {
  externalAccounts: ExternalAccountListItem[];
  externalAccountsIsPending: boolean;
  setOpenExternalAccountModal: React.Dispatch<React.SetStateAction<boolean>>;
  createBankApplication: UseMutateAsyncFunction<
    BankApplicationDetails,
    Error,
    BankApplicationFormValues,
    unknown
  >;
};

export default function BankAccountApplicationExternalAccounts({
  externalAccounts,
  externalAccountsIsPending,
  setOpenExternalAccountModal,
  createBankApplication,
}: BankAccountApplicationExternalAccountsProps) {
  const formMethods = useFormContext<BankApplicationFormValues>();

  async function handleAddExternalAccount() {
    const data = formMethods.getValues();

    if (!data.applicationId) {
      const response = await createBankApplication(data);
      formMethods.setValue('applicationId', response.uuid);
    }

    setOpenExternalAccountModal(true);
  }

  return (
    <Card>
      <Typography variant="h5">External Accounts</Typography>
      {externalAccountsIsPending ? (
        <SVLoader />
      ) : (
        <>
          {!externalAccounts.length ? (
            <Typography>
              The account owners do not have any linked external accounts.
            </Typography>
          ) : (
            <>
              {externalAccounts.map(externalAccount => (
                <Box
                  key={externalAccount.uuid}
                  sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                  <Typography>{externalAccount.accountName}</Typography>
                  <ExternalAccountStatusLabel status={externalAccount.status} />
                </Box>
              ))}
            </>
          )}
        </>
      )}
      <SVButton
        onClick={handleAddExternalAccount}
        variant="outlined"
        sx={{ mt: 2 }}>
        Link External Account
      </SVButton>
    </Card>
  );
}
